import { EditOutlined, CopyOutlined, CheckOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Space, Tooltip, message, Switch, Modal } from 'antd';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_PROFILE } from '../../../../constants/define/translation';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { UserContext } from '../../../../repository/context/userContext';
import { CountryFullContext } from '../../../../repository/context/countryFullContext';
import {
  postActiveUserRepository,
  deleteUserRepository,
  deleteUserBlackListRepository,
  postAddUserBlacklistRepository,
} from '../../../../repository/mytm/user/userMeRepository';
import UpdateUserCountryModal from '../UpdateUserCountryModal';

export default React.memo((props: any) => {
  const { t } = useTranslation(USER_PROFILE);
  const { record, setRefresh, setIsLoading } = props;
  const messageSuccess = 'Copied email to the clipboard!';
  const userContext = useContext(UserContext);
  const countryFullContext = useContext(CountryFullContext);
  const [copied, setCopied] = useState(false);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const countries: any[] = countryFullContext?.countryListFull?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });

  const canEdit = record?.action === 'EDIT';
  const canCopy = record?.action === 'COPY';
  const canToggleBlackList = record?.action === 'TOGGLE_BLACKLIST';
  const canToggleActive = record?.action === 'TOGGLE_ACTIVE';
  const canView = record?.action === 'VIEW';

  const handleCopyToClipboard = useCallback((textCopy: any) => {
    setCopied(true);
    navigator.clipboard.writeText(textCopy);

    message.success(messageSuccess).then(r => r);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  }, []);

  const handleViewUrl = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
    return false;
  };

  const updateFunc = () => {
    if (record?.deletedAt) {
      postActiveUserRepository(record?.userId)
        .then(r => {
          setIsLoading(false);
          message.success(t('message.activeUser'));
          setRefresh(true);
        })
        .catch(e => {
          setIsLoading(false);
          message.error(e?.errorMessage || t('err.message.activeUser'));
        });
    } else {
      deleteUserRepository(record?.userId)
        .then(r => {
          setIsLoading(false);
          message.success(t('message.removeUser'));
          setRefresh(true);
        })
        .catch(e => {
          setIsLoading(false);
          message.error(e?.errorMessage || t('err.message.activeUser'));
        });
    }
  };

  const updateFuncUserBlacklist = () => {
    if (record.userBlackListId) {
      deleteUserBlackListRepository(record.userBlackListId)
        .then(r => {
          setBlacklistLoading(false);
          message.success(t('message.removeBlacklist'));
          setRefresh(true);
        })
        .catch(e => {
          setBlacklistLoading(false);
          message.error(e?.errorMessage || t('err.message.removeBlacklist'));
        });
    } else {
      postAddUserBlacklistRepository({
        email: record?.email,
      })
        .then(r => {
          setBlacklistLoading(false);
          message.success(t('message.addBlacklist'));
          setRefresh(true);
        })
        .catch(e => {
          setBlacklistLoading(false);
          message.error(e?.errorMessage || t('err.message.addBlacklist'));
        });
    }
  };

  const onUpdateUser = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: record?.userBlackListId ? (
        <ConfirmAction
          textConfirmAction={t('table.popup.removeBlacklist')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ) : (
        <ConfirmAction
          textConfirmAction={t('table.popup.addBlacklist')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setIsLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;

        if (!userContext.user?.email || userContext.user?.email === '') {
          setIsLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setIsLoading(false);
              message.error(e?.errorMessage || t('err.message.login'));
            });
        }
      },
    });
  };

  const onUpdateUserBlacklist = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: record?.us ? (
        <ConfirmAction
          textConfirmAction={t('table.popup.activeUser')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ) : (
        <ConfirmAction
          textConfirmAction={t('table.popup.removeUser')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setBlacklistLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;

        if (!userContext.user?.email || userContext.user?.email === '') {
          setBlacklistLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFuncUserBlacklist();
            })
            .catch((e: any) => {
              setBlacklistLoading(false);
              message.error(e?.errorMessage || t('err.message.login'));
            });
        }
      },
    });
  };

  const onUpdateUserCountry = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.updateUserCountry')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setIsLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;

        if (!userContext.user?.email || userContext.user?.email === '') {
          setIsLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              setIsModalVisible(true);
              setIsLoading(false);
            })
            .catch((e: any) => {
              setIsLoading(false);
              setIsModalVisible(false);
              message.error(e?.errorMessage || t('err.message.login'));
            });
        }
      },
    });
  };

  return (
    <>
      <StyledContainer>
        <Space size="small">
          {canEdit && (
            <Tooltip title={t('table.tooltip.edit')}>
              <Button icon={<EditOutlined />} onClick={onUpdateUserCountry} />
            </Tooltip>
          )}
          {canView && record.url && (
            <Tooltip title={t('table.tooltip.view')}>
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  handleViewUrl(record?.url);
                }}
              />
            </Tooltip>
          )}
          {canCopy && (
            <Tooltip title={t('table.tooltip.copy')}>
              <Button
                onClick={() => {
                  handleCopyToClipboard(record?.value);
                }}
                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                style={{ color: copied ? 'green' : 'black' }}
              />
            </Tooltip>
          )}
          {canToggleActive && (
            <Tooltip title={record.deletedAt ? t('table.tooltip.activeUser') : t('table.tooltip.removeUser')}>
              <Switch checked={!record.deletedAt} onChange={onUpdateUser} />
            </Tooltip>
          )}
          {canToggleBlackList && (
            <Tooltip
              title={record.userBlackListId ? t('table.tooltip.removeBlacklist') : t('table.tooltip.addBlacklist')}
            >
              <Switch
                checked={record.userBlackListId}
                onChange={onUpdateUserBlacklist}
                disabled={blacklistLoading}
                loading={blacklistLoading}
              />
            </Tooltip>
          )}
        </Space>
      </StyledContainer>
      {isModalVisible && (
        <UpdateUserCountryModal
          visible={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          countries={countries}
          userId={record?.userId}
          defaultCountry={record.value}
          onRefresh={() => setRefresh(true)}
        />
      )}
    </>
  );
});

const StyledContainer = styled.div``;
